import type { ParsedUrlQuery } from "querystring";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/router";
import * as yup from "yup";
import { Dictionary, isEmpty, keyBy, omit, pick } from "lodash";
import { captureException } from "@sentry/nextjs";
import {
  getAppliedFiltersSchema,
  getSchemaForDef,
} from "components/Shared/FilterEditor/schema";
import useLatestRef from "utils/useLatestRef";
import getResults from "components/Shared/FilterEditor/getResults";
import { asyncVoid } from "utils/asyncVoidHandler";
import { getPathnameParams } from "utils/routeParamHooks";
import {
  FilterDefinition,
  FilterVariantDefinition,
  AppliedFilter,
} from "./utils";

const PARAM_KEY_SEPARATOR = "__";

function getFiltersAndVariantsByIds(filterDefinitions: FilterDefinition[]) {
  return filterDefinitions.reduce<
    Dictionary<
      FilterDefinition & {
        variantsById: Dictionary<FilterVariantDefinition>;
      }
    >
  >((acc, fd) => {
    acc[fd.id] = { ...fd, variantsById: keyBy(fd.variants, "id") };
    return acc;
  }, {});
}

export function getAppliedFiltersUrlQuery(
  filterDefinitions: FilterDefinition[],
  filters: AppliedFilter[],
) {
  const filtersAndVariantsByIds = getFiltersAndVariantsByIds(filterDefinitions);

  return filters.reduce<ParsedUrlQuery>((acc, appliedFilter) => {
    const { filter, variant, value } = appliedFilter;

    const fd = filtersAndVariantsByIds[filter];
    const fvd = fd?.variantsById?.[variant];

    const isEqualityVariant =
      !!fd?.equalityVariant && !!fvd?.id && fvd.id === fd.equalityVariant;

    const paramKey = isEqualityVariant
      ? filter
      : `${filter}${PARAM_KEY_SEPARATOR}${variant}`;

    const paramValue = Array.isArray(value) ? value.map(String) : String(value);

    if (acc[paramKey]) {
      let existing = acc[paramKey];
      if (!Array.isArray(existing)) {
        existing = existing ? [existing] : [];
      }
      if (Array.isArray(paramValue)) {
        existing.push(...paramValue);
      } else {
        existing.push(paramValue);
      }
      acc[paramKey] = existing;
    } else {
      acc[paramKey] = paramValue;
    }
    return acc;
  }, {});
}

export function parseAppliedFiltersUrlQuery(
  filterDefinitions: FilterDefinition[],
  query: ParsedUrlQuery,
) {
  const filtersAndVariantsByIds = getFiltersAndVariantsByIds(filterDefinitions);

  const filterKeys = filterDefinitions.map((fd) => fd.id);

  const getFilterVariant = (
    paramKey: string,
  ): [filter?: string, variant?: string] => {
    const [paramFilter, paramVariant] = (() => {
      if (filterKeys.includes(paramKey)) return [paramKey];

      const splitParam = paramKey.split(PARAM_KEY_SEPARATOR);

      return [
        splitParam.slice(0, -1).join(PARAM_KEY_SEPARATOR),
        splitParam.slice(-1)[0],
      ];
    })();

    if (paramFilter && paramVariant) return [paramFilter, paramVariant];

    if (paramFilter) {
      const fd = filtersAndVariantsByIds[paramFilter];
      if (fd?.equalityVariant) return [paramFilter, fd.equalityVariant];
    }

    return [];
  };

  return Object.keys(query).reduce<AppliedFilter[]>((acc, paramKey) => {
    const [filter, variant] = getFilterVariant(paramKey);

    if (!filter || !variant) return acc;

    const fd = filtersAndVariantsByIds[filter];
    const fvd = fd?.variantsById?.[variant];

    if (!fd || !fvd) return acc;

    const paramValue = query[paramKey];

    const schema = getSchemaForDef(fd, fvd);

    if (Array.isArray(paramValue) && !(schema instanceof yup.ArraySchema)) {
      paramValue.forEach((v) => {
        const value = schema ? (schema.cast(v) as unknown) : v;
        acc.push({ filter, variant, value });
      });
    } else {
      const value = schema ? (schema.cast(paramValue) as unknown) : paramValue;
      acc.push({ filter, variant, value });
    }

    return acc;
  }, []);
}

export function defaultProcessResults<T, R = T>(results: T[]) {
  return results as R;
}

export type UseFiltersUrlQueryOptions<T, R = T> = {
  defaultFilters?: AppliedFilter[];
  externalQueryKeys?: string[];
  isDefaultFiltersReady?: boolean;
  processResults?: (results: T[]) => R | undefined;
};

export function useFiltersUrlQuery<T, R = T>(
  filterDefinitions: FilterDefinition[],
  {
    defaultFilters,
    externalQueryKeys,
    isDefaultFiltersReady = true,
    processResults = defaultProcessResults,
  }: UseFiltersUrlQueryOptions<T, R> = {},
) {
  const router = useRouter();

  const latestExternalQueryKeysRef = useLatestRef(externalQueryKeys);

  const getInitialQuery = useCallback(() => {
    if (!router.isReady) return;

    const externalKeys = [
      ...getPathnameParams(router.pathname),
      ...(latestExternalQueryKeysRef?.current || []),
    ];

    if (externalKeys?.length) return omit(router.query, externalKeys);

    return router.query;
  }, [latestExternalQueryKeysRef, router]);

  const [initialQuery, setInitialQuery] = useState<ParsedUrlQuery | undefined>(
    getInitialQuery,
  );
  useEffect(() => {
    if (router.isReady && !initialQuery) {
      setInitialQuery(getInitialQuery());
    }
  }, [router, initialQuery, getInitialQuery]);
  const [isTableReady, setIsTableReady] = useState(false);

  const initialFilters = useMemo(() => {
    if (!initialQuery || !isDefaultFiltersReady) return;

    if (isEmpty(initialQuery) && defaultFilters) {
      return defaultFilters;
    }

    return parseAppliedFiltersUrlQuery(filterDefinitions, initialQuery);
  }, [filterDefinitions, initialQuery, isDefaultFiltersReady, defaultFilters]);

  const [currentFilters, setCurrentFilters] = useState(initialFilters || []);

  useEffect(() => {
    if (initialFilters) {
      setCurrentFilters(initialFilters);
      setIsTableReady(true);
    }
  }, [initialFilters]);

  const filtersSchema = useMemo(
    () => getAppliedFiltersSchema(filterDefinitions),
    [filterDefinitions],
  );

  const latestProcessResultsRef = useLatestRef(processResults);

  const filter = useMemo(() => {
    try {
      filtersSchema.validateSync(currentFilters);
    } catch (e) {
      console.error(e);
      captureException(e);
      return;
    }

    const results: T[] = [];

    if (currentFilters?.length) {
      try {
        const actualResults = getResults<T>(filterDefinitions, currentFilters);
        results.push(...actualResults);
      } catch (e) {
        console.error(e);
        captureException(e);
      }
    }

    return latestProcessResultsRef.current(results);
  }, [
    filterDefinitions,
    filtersSchema,
    currentFilters,
    latestProcessResultsRef,
  ]);

  const latestQueryRef = useLatestRef(router.query);

  const handleFiltersChange = useCallback(
    (filters: AppliedFilter[]) => {
      const filtersQuery = getAppliedFiltersUrlQuery(
        filterDefinitions,
        filters,
      );
      const externalKeys = [
        ...getPathnameParams(router.pathname),
        ...(latestExternalQueryKeysRef?.current || []),
      ];
      asyncVoid(
        router.replace(
          {
            query: externalKeys?.length
              ? {
                  ...omit(filtersQuery, externalKeys),
                  ...pick(latestQueryRef.current, externalKeys),
                }
              : filtersQuery,
          },
          undefined,
          { shallow: true },
        ),
      );
      setCurrentFilters(filters);
    },
    [filterDefinitions, latestExternalQueryKeysRef, latestQueryRef, router],
  );

  return {
    filter,
    filters: currentFilters,
    handleFiltersChange,
    isTableReady,
  };
}

export function graphileProcessResults<T, R = T>(results: T[]) {
  if (results.length === 0) return;
  return { and: results } as R;
}

export function useGraphileFiltersUrlQuery<T, R = T>(
  filterDefinitions: FilterDefinition[],
  options: UseFiltersUrlQueryOptions<T, R> = {},
) {
  return useFiltersUrlQuery<T, R>(filterDefinitions, {
    processResults: graphileProcessResults,
    ...options,
  });
}
